<template>
    <div>
        <md-dialog :md-active.sync="showAddMeter" class="add-meter-dialog">
            <md-dialog-title class="dialog-title">{{ $tc('phrases.addMeter') }}</md-dialog-title>
            <md-dialog-content class="dialog-body">
                <form class="md-layout md-gutter" data-vv-scope="meter-add-form">
                    <section class="md-layout-item md-size-100 form-section">
                        <h3 class="section-heading">{{ $tc('phrases.meterInformation') }}</h3>
                        <div class="md-layout md-gutter">
                            <div class="md-layout-item md-size-50 md-small-size-100 customer-select">
                                <label class="customer-label">{{ $tc('words.customer') }}</label>
                                <multiselect
                                    v-model="selectedPerson"
                                    :options="personService.list"
                                    :custom-label="customerLabel"
                                    track-by="id"
                                    :placeholder="$tc('words.search')"
                                    :show-labels="false"
                                    :allow-empty="true"
                                    :internal-search="false"
                                    :loading="customerLoading"
                                    :options-limit="300"
                                    @search-change="searchCustomers"
                                >
                                    <span slot="noResult">No results found</span>
                                </multiselect>
                                <input type="hidden" name="customer" v-validate="'required'" v-model="selectedPersonId"/>
                                <span class="md-error" v-if="errors.has('meter-add-form.customer')">
                                    {{ errors.first('meter-add-form.customer') }}
                                </span>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.serial_number')}">
                                    <label for="serial_number">{{ $tc('phrases.serialNumber') }}</label>
                                    <md-input
                                        id="serial_number"
                                        name="serial_number"
                                        v-model="form.serialNumber"
                                        v-validate="'required'"
                                        placeholder="###########"
                                    />
                                    <span class="md-error">{{ errors.first('meter-add-form.serial_number') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.manufacturer')}">
                                    <label for="manufacturer">{{ $tc('words.manufacturer') }}</label>
                                    <md-select
                                        id="manufacturer"
                                        name="manufacturer"
                                        v-model="form.manufacturerId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="manufacturer in meterManufacturers"
                                            :key="manufacturer.id"
                                            :value="manufacturer.id"
                                        >
                                            {{ manufacturer.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.manufacturer') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.meter_type')}">
                                    <label for="meter_type">{{ $tc('phrases.meterType') }}</label>
                                    <md-select
                                        id="meter_type"
                                        name="meter_type"
                                        v-model="form.meterTypeId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="meterType in meterTypeService.meterTypesList"
                                            :key="meterType.id"
                                            :value="meterType.id"
                                        >
                                            {{ meterType.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.meter_type') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.tariff')}">
                                    <label for="tariff">{{ $tc('words.tariff') }}</label>
                                    <md-select
                                        id="tariff"
                                        name="tariff"
                                        v-model="form.tariffId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="tariff in tariffService.list"
                                            :key="tariff.id"
                                            :value="tariff.id"
                                        >
                                            {{ tariff.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.tariff') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.village')}">
                                    <label for="village">{{ $tc('words.village') }}</label>
                                    <md-select
                                        id="village"
                                        name="village"
                                        v-model="selectedAddressId"
                                        v-validate="'required'"
                                        :disabled="!selectedPersonId"
                                    >
                                        <md-option
                                            v-for="address in customerAddresses"
                                            :key="address.id"
                                            :value="address.id"
                                        >
                                            {{ addressLabel(address) }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.village') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.connection_group')}">
                                    <label for="connection_group">{{ $tc('phrases.connectionGroup') }}</label>
                                    <md-select
                                        id="connection_group"
                                        name="connection_group"
                                        v-model="form.connectionGroupId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="group in connectionGroupService.list"
                                            :key="group.id"
                                            :value="group.id"
                                        >
                                            {{ group.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.connection_group') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.connection_type')}">
                                    <label for="connection_type">{{ $tc('phrases.connectionType') }}</label>
                                    <md-select
                                        id="connection_type"
                                        name="connection_type"
                                        v-model="form.connectionTypeId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="type in connectionTypeService.list"
                                            :key="type.id"
                                            :value="type.id"
                                        >
                                            {{ type.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.connection_type') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-50 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.sub_connection_type')}">
                                    <label for="sub_connection_type">{{ $tc('phrases.subConnectionType') }}</label>
                                    <md-select
                                        id="sub_connection_type"
                                        name="sub_connection_type"
                                        v-model="form.subConnectionTypeId"
                                        :disabled="!form.connectionTypeId"
                                        v-validate="'required'"
                                    >
                                        <md-option
                                            v-for="subType in subConnectionTypeService.subConnectionTypes"
                                            :key="subType.id"
                                            :value="subType.id"
                                        >
                                            {{ subType.name }}
                                        </md-option>
                                    </md-select>
                                    <span class="md-error">{{ errors.first('meter-add-form.sub_connection_type') }}</span>
                                </md-field>
                            </div>
                        </div>
                    </section>

                    <section class="md-layout-item md-size-100 form-section">
                        <h3 class="section-heading">{{ $tc('words.location') }}</h3>
                        <div class="md-layout md-gutter">
                            <div class="md-layout-item md-size-30 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.latitude')}">
                                    <label for="latitude">{{ $tc('words.latitude') }}</label>
                                    <md-input
                                        id="latitude"
                                        name="latitude"
                                        v-model="form.lat"
                                        step="any"
                                        v-validate="'required|decimal:5'"
                                    />
                                    <span class="md-error">{{ errors.first('meter-add-form.latitude') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-30 md-small-size-100">
                                <md-field :class="{'md-invalid': errors.has('meter-add-form.longitude')}">
                                    <label for="longitude">{{ $tc('words.longitude') }}</label>
                                    <md-input
                                        id="longitude"
                                        name="longitude"
                                        v-model="form.lon"
                                        step="any"
                                        v-validate="'required|decimal:5'"
                                    />
                                    <span class="md-error">{{ errors.first('meter-add-form.longitude') }}</span>
                                </md-field>
                            </div>
                            <div class="md-layout-item md-size-20 md-small-size-50">
                                <md-button type="button" class="md-primary set-button" @click="setPoints">
                                    {{ $tc('phrases.setPoints') }}
                                </md-button>
                            </div>
                            <div class="md-layout-item md-size-20 md-small-size-50">
                                <md-button
                                    type="button"
                                    class="md-primary save-button"
                                    :disabled="loading"
                                    @click="save"
                                >
                                    {{ $tc('words.save') }}
                                </md-button>
                            </div>
                            <div class="md-layout-item md-size-100 map-area">
                                <meter-map
                                    v-if="showAddMeter"
                                    ref="meterMapRef"
                                    :mapping-service="mappingService"
                                    :marker="true"
                                    @locationSet="meterLocationSet"
                                />
                            </div>
                        </div>
                    </section>
                </form>
            </md-dialog-content>
            <md-dialog-actions class="dialog-actions">
                <md-button class="md-raised" @click="cancel">{{ $tc('words.close') }}</md-button>
            </md-dialog-actions>
            <md-progress-bar md-mode="indeterminate" v-if="loading"/>
        </md-dialog>
    </div>
</template>

<script>
import { notify } from '@/mixins'
import { PersonService } from '@/services/PersonService'
import { ManufacturerService } from '@/services/ManufacturerService'
import { MeterTypeService } from '@/services/MeterTypeService'
import { TariffService } from '@/services/TariffService'
import { ConnectionGroupService } from '@/services/ConnectionGroupService'
import { ConnectionTypeService } from '@/services/ConnectionTypeService'
import { SubConnectionTypeService } from '@/services/SubConnectionTypeService'
import { MeterService } from '@/services/MeterService'
import { MiniGridService } from '@/services/MiniGridService'
import { ClusterService } from '@/services/ClusterService'
import { ICONS, MappingService, MARKER_TYPE } from '@/services/MappingService'
import MeterMap from '@/modules/Map/MeterMap.vue'
import Multiselect from 'vue-multiselect'

const debounce = require('debounce')

export default {
    name: 'AddMeterModal',
    mixins: [notify],
    components: { MeterMap, Multiselect },
    props: {
        showAddMeter: {
            required: true,
            type: Boolean
        }
    },
    data () {
        return {
            personService: new PersonService(),
            manufacturerService: new ManufacturerService(),
            meterTypeService: new MeterTypeService(),
            tariffService: new TariffService(),
            connectionGroupService: new ConnectionGroupService(),
            connectionTypeService: new ConnectionTypeService(),
            subConnectionTypeService: new SubConnectionTypeService(),
            meterService: new MeterService(),
            miniGridService: new MiniGridService(),
            clusterService: new ClusterService(),
            mappingService: new MappingService(),
            loading: false,
            customerLoading: false,
            selectedPerson: null,
            selectedAddressId: null,
            customerAddresses: [],
            form: {
                serialNumber: null,
                manufacturerId: null,
                meterTypeId: null,
                tariffId: null,
                connectionGroupId: null,
                connectionTypeId: null,
                subConnectionTypeId: null,
                cityId: null,
                street: null,
                lat: null,
                lon: null
            }
        }
    },
    computed: {
        selectedPersonId () {
            return this.selectedPerson?.id || null
        },
        meterManufacturers () {
            return (this.manufacturerService.list || []).filter(manufacturer => manufacturer.type === 'meter')
        }
    },
    created () {
        this.searchCustomers = debounce(this.fetchCustomers, 300)
    },
    beforeMount () {
        this.mappingService.setMarkerUrl(ICONS.METER)
        this.loadLookups()
    },
    methods: {
        async loadLookups () {
            try {
                await Promise.all([
                    this.personService.getPeople(),
                    this.manufacturerService.getManufacturers(),
                    this.meterTypeService.getMeterTypes(),
                    this.tariffService.getTariffs(),
                    this.connectionGroupService.getConnectionGroups(),
                    this.connectionTypeService.getConnectionTypes()
                ])
            } catch (e) {
                this.alertNotify('error', e.message)
            }
        },
        addressLabel (address) {
            const cityName = address?.city?.name || ''
            const street = address?.street || ''
            return `${cityName} ${street}`.trim()
        },
        customerLabel (person) {
            if (!person) return ''
            const name = `${person.name || ''} ${person.surname || ''}`.trim()
            const phone = person.addresses?.[0]?.phone
            return phone ? `${name} (${phone})` : name
        },
        async fetchCustomers (query) {
            this.customerLoading = true
            try {
                if (!query) {
                    await this.personService.getPeople()
                } else {
                    await this.personService.searchPeople(query)
                }
            } catch (e) {
                this.alertNotify('error', e.message)
            }
            this.customerLoading = false
        },
        async save () {
            const validator = await this.$validator.validateAll('meter-add-form')
            if (!validator) return
            this.loading = true
            try {
                const meter = await this.meterService.createMeter({
                    serial_number: this.form.serialNumber,
                    manufacturer_id: this.form.manufacturerId,
                    meter_type_id: this.form.meterTypeId,
                    tariff_id: this.form.tariffId,
                    connection_group_id: this.form.connectionGroupId,
                    sub_connection_type_id: this.form.subConnectionTypeId,
                    person_id: this.selectedPersonId,
                    city_id: this.form.cityId,
                    street: this.form.street,
                    geo_points: `${this.form.lat},${this.form.lon}`
                })
                this.alertNotify('success', this.$tc('messages.successfullyCreated', { item: this.$tc('words.meter', 1) }))
                this.$emit('created', meter)
                await this.$router.push(`/meters/${meter.serial_number}`)
            } catch (e) {
                this.alertNotify('error', e.message)
            }
            this.loading = false
        },
        cancel () {
            this.$emit('hideAddMeter')
        },
        setPoints () {
            if (!this.form.lat || !this.form.lon) return
            const location = [this.form.lat, this.form.lon]
            this.$refs.meterMapRef.setMeterMarkerManually(location)
        },
        meterLocationSet (data) {
            if (!data.error) {
                this.form.lat = Number(data.geoDataItem.coordinates.lat.toFixed(5))
                this.form.lon = Number(data.geoDataItem.coordinates.lng.toFixed(5))
            } else {
                this.form.lat = null
                this.form.lon = null
                this.$swal({
                    type: 'warning',
                    text: data.error
                })
            }
        },
        async loadVillageMap (address) {
            const city = address?.city
            const pointsStr = city?.location?.points
            if (pointsStr) {
                const points = pointsStr.split(',')
                if (points.length === 2) {
                    this.form.lat = Number(parseFloat(points[0]).toFixed(5))
                    this.form.lon = Number(parseFloat(points[1]).toFixed(5))
                }
            }
            const miniGridId = city?.mini_grid_id
            if (!miniGridId || !this.$refs.meterMapRef) return
            try {
                const miniGridWithGeoData = await this.miniGridService.getMiniGridGeoData(miniGridId)
                const miniGridPoints = miniGridWithGeoData?.location?.points
                if (!miniGridPoints) return
                const points = miniGridPoints.split(',')
                if (points.length !== 2) return
                const lat = parseFloat(points[0])
                const lon = parseFloat(points[1])
                const clusterId = miniGridWithGeoData.cluster_id
                const clusterGeoData = await this.clusterService.getClusterGeoLocation(clusterId)
                this.mappingService.setCenter([clusterGeoData.lat, clusterGeoData.lon])
                this.mappingService.setGeoData(clusterGeoData)
                this.mappingService.setMarkingInfos([{
                    id: miniGridWithGeoData.id,
                    name: miniGridWithGeoData.name,
                    serialNumber: null,
                    lat: lat,
                    lon: lon,
                    dataStream: -1,
                    deviceType: null,
                    markerType: MARKER_TYPE.MINI_GRID
                }])
                this.$refs.meterMapRef.drawCluster()
                this.$refs.meterMapRef.setMiniGridMarker()
                this.$nextTick(() => {
                    if (this.form.lat && this.form.lon) {
                        this.setPoints()
                    }
                })
            } catch (e) {
                this.alertNotify('error', e.message)
            }
        }
    },
    watch: {
        showAddMeter (visible) {
            if (visible) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$refs.meterMapRef && this.$refs.meterMapRef.invalidateSize()
                    }, 300)
                })
            }
        },
        async selectedPersonId (personId) {
            this.selectedAddressId = null
            this.customerAddresses = []
            this.form.cityId = null
            this.form.street = null
            if (!personId) return
            try {
                const person = await this.personService.getPerson(personId)
                this.customerAddresses = person.addresses || []
            } catch (e) {
                this.alertNotify('error', e.message)
            }
        },
        async selectedAddressId (addressId) {
            const address = this.customerAddresses.find((item) => item.id === addressId)
            this.form.cityId = address?.city_id || address?.city?.id || null
            this.form.street = address?.street || null
            if (address) {
                await this.$nextTick()
                this.loadVillageMap(address)
            }
        },
        async 'form.connectionTypeId' (connectionTypeId) {
            this.form.subConnectionTypeId = null
            this.subConnectionTypeService.subConnectionTypes = []
            if (!connectionTypeId) return
            try {
                await this.subConnectionTypeService.getSubConnectionTypes(connectionTypeId)
            } catch (e) {
                this.alertNotify('error', e.message)
            }
        }
    }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.add-meter-dialog {
    max-width: 72rem;
    width: 100%;
    margin: auto;
}

.dialog-title {
    font-size: 1.25rem;
    font-weight: 600;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
}

.dialog-body {
    overflow-y: auto;
    padding-top: 8px;
}

.form-section + .form-section {
    margin-top: 8px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

.section-heading {
    margin: 0 0 12px;
    padding-left: 10px;
    border-left: 3px solid #4caf50;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #616161;
}

.dialog-actions {
    border-top: 1px solid #e0e0e0;
    padding-top: 8px;
}

.set-button {
    background-color: #448aff !important;
    color: #fefefe !important;
    margin-top: 0.5rem;
}

.save-button {
    background-color: #325932 !important;
    color: #fefefe !important;
    margin-top: 0.5rem;
    float: right;
}

.map-area {
    z-index: 1 !important;
    margin-top: 8px;
}

.customer-select {
    position: relative;
    z-index: 20;
    margin-bottom: 16px;
}

.customer-label {
    display: block;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.54);
    margin-bottom: 6px;
}

.customer-select >>> .multiselect {
    min-height: 36px;
}

.customer-select >>> .multiselect__tags {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 0;
    background: transparent;
    min-height: 36px;
    padding: 6px 40px 0 0;
}

.customer-select >>> .multiselect__placeholder,
.customer-select >>> .multiselect__single {
    margin: 0;
    padding: 0;
    font-size: 16px;
}

.customer-select >>> .multiselect__content-wrapper {
    z-index: 30;
}

.md-error {
    color: #ff1744;
    font-size: 12px;
}
</style>
